import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Fragment, useContext, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DonutCustomLegend from '../../components/ApexCharts/CustomLegends/DonutCustomLegend';
import DonutChart from '../../components/ApexCharts/DonutChart';
import DefaultCard from '../../components/DefaultCard';
import ExpandableCard from '../../components/ExpandableCard';
import FingerTipsLinks from '../../components/FingertipsLinks/FingertipsLinks';
import GenericList, { GenericListItem } from '../../components/GenericList';
import IconButton from '../../components/IconButton';
import Label from '../../components/Label';
import Popover from '../../components/Popover';
import { AuthStore } from '../../providers/auth';
import { DashboardProvider, DashboardStore } from '../../providers/dashboard';
import {
  LIFE_EVENTS_ROUTE,
  PROFILE_ROUTE,
  TOTAL_REWARDS_ROUTE,
} from '../../routes/types';
import {
  addOrRemoveDaysToGivenDate,
  DateFormats,
  formatDate,
  formatPrice,
} from '../../utils/utils';
import PopoverWithLinks from '../widgets/PopoverLinks/PopoverLinks';

const getCardIcon = (type: String) => {
  switch (type) {
    case 'heart-pulse':
      return light('heart-pulse');
    case 'tooth':
      return light('tooth');
    case 'glasses':
      return light('glasses');
    default:
      return light('glasses');
  }
};

const linksData = [
  {
    linkHeader: 'More Details',
    route: '#',
  },

  {
    linkHeader: 'Change Coverage',
    route: `${LIFE_EVENTS_ROUTE}`,
  },

  {
    linkHeader: 'Payment Information',
    route: '#',
  },

  {
    linkHeader: 'Beneficiaries',
    route: `${PROFILE_ROUTE.slice(0, 9)}beneficiaries`,
  },

  {
    linkHeader: 'Family Members',
    route: `${PROFILE_ROUTE.slice(0, 9)}familymembers`,
  },
];

const dataCompanyContributions = {
  // The "grid -> padding" is a hack to reduce the huge margins that donut renders.
  // See: https://github.com/apexcharts/apexcharts.js/issues/1272
  grid: {
    padding: {
      left: -20,
      top: -10,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        grid: {
          padding: {
            left: 0,
            top: 10,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#F48462', '#07B4A0', '#EEA968', '#92E3A9'],
  series: [95072, 15905, 14791, 6500, 0],
  labels: [
    'Compensation',
    'Health and Welfare',
    'Retirement',
    'Equity',
    'Other Benefits',
  ],

  tooltip: {
    enabled: false,
  },

  // DISPLAY TEXT IN DONUT CENTRE:
  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$132,268`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

function DashboardView() {
  const [, ,] = useContext(AuthStore) || [];
  const {
    enrollment,
    currentLifeEvent,
    healthSnapshot,
    healthSavings,
    fingertipLinks,
  } = useContext(DashboardStore) || {};

  const [showTotalRewardsChart, setShowTotalRewardsChart] = useState(false);
  const toggleChart = () => {
    setShowTotalRewardsChart(!showTotalRewardsChart);
  };

  const navigate = useNavigate();

  const dateDue = formatDate(
    addOrRemoveDaysToGivenDate(new Date(), 14),
    DateFormats.LONG
  );
  const dateDeadline = formatDate(addOrRemoveDaysToGivenDate(new Date(), 14));

  return (
    <Container className={'cnd-custom-container-large mb-12 px-lg-9 px-xl-0'}>
      <Row>
        <Col className="mt-7 mt-lg-11 col-12 col-lg-7">
          <DefaultCard
            className="h-100"
            variant="blue-motif"
            leftHeading={enrollment['title']}
            leftSubheading={`Due: ${dateDue}`}
            button={
              <Button
                variant="primary-shadow"
                size="sm"
                onClick={() => navigate('/health-and-insurance/summary')}
              >
                Enroll Now
              </Button>
            }
          />
        </Col>
        <Col className="mt-7 mt-lg-11 col-12 col-lg-5">
          <DefaultCard
            variant="blue"
            leftHeading={'Current Events'}
            leftSubheading={
              <Fragment>
                <div className="text-sm-12-lg-14">
                  <strong>{`${currentLifeEvent['type']}`}</strong>
                </div>
                <div className="text-sm-12-lg-14 fw-light">{`Deadline: ${dateDeadline}`}</div>
                <div className="text-sm-12-lg-14 fw-light">{`Status: ${currentLifeEvent['status']}`}</div>
              </Fragment>
            }
            button={
              <Button variant="primary-shadow" size="sm">
                Edit
              </Button>
            }
          />
        </Col>
      </Row>
      <Row>
        <Col className="mt-12 pt-lg-3 col-12 d-flex justify-content-between">
          <h4 className="h4">Health Snapshot</h4>
          <PopoverWithLinks linksData={linksData} />
        </Col>
      </Row>
      <Row className="mt-10 mt-lg-0">
        {healthSnapshot.map((snapshot: any, index) => (
          <Col className="mt-lg-10 col-12 col-lg-4" key={index}>
            <ExpandableCard
              title={snapshot['generic']['title']}
              subtitle={snapshot['plan']['title']}
              avatars={snapshot['profiles']}
              cardIcon={getCardIcon(snapshot['generic']['icon'])}
              iconOnClick={(name) => console.log(name)}
            />
          </Col>
        ))}
      </Row>
      <Row>
        <Col className="mt-lg-12 pt-lg-4 col-12 d-flex justify-content-between">
          <h4 className="h4">Health Savings & Spending</h4>
          <div>
            <Popover
              trigger="click"
              placement="bottom-end"
              popoverBody={
                <GenericList>
                  <GenericListItem>
                    <Label rightIcon={light('circle-question')}>
                      Lorem ipsum
                    </Label>
                  </GenericListItem>
                </GenericList>
              }
            >
              <IconButton variant="icon-default" icon={light('ellipsis')} />
            </Popover>
          </div>
        </Col>
      </Row>
      <Row className="mt-3 mt-lg-0">
        {healthSavings.map((snapshot: any, index) => (
          <Col className="mt-7 mt-lg-10 col-12 col-lg-4" key={index}>
            <DefaultCard
              leftHeading={snapshot['title']}
              leftSubheading={snapshot['description']}
              rightHeading={formatPrice(snapshot['amount'])}
              rightSubheading={snapshot['amointSubtitle']}
              button={
                <Button variant="primary-outline" size="sm">
                  Details
                </Button>
              }
            />
          </Col>
        ))}
      </Row>
      <Row className="mb-12 pb-12">
        <Col className="mt-12 col-12 col-lg-6">
          <div className="d-flex justify-content-between">
            <h4 className="h4">Total Rewards</h4>
            <div className="d-flex">
              <IconButton
                variant="icon-emphasized"
                icon={light('pie-chart')}
                className="me-7"
                onClick={toggleChart}
              />
              <IconButton
                variant="icon-default"
                icon={light('ellipsis')}
                onClick={() => navigate(TOTAL_REWARDS_ROUTE)}
              />
            </div>
          </div>
          <div className="mt-7 mt-lg-12 p-10 border border-info rounded-1 px-12">
            <Row>
              <Col className="col-lg-10">
                <Row>
                  <h5>Company's Contribution</h5>
                </Row>
                <Row>
                  <span className="text-light-grey text-fs-6 fw-light">
                    {`(${formatDate(
                      new Date(),
                      DateFormats.ONLY_YEAR
                    )} Annualized)`}
                  </span>
                </Row>
              </Col>
            </Row>
            {showTotalRewardsChart ? (
              <Col className="d-flex justify-content-center">
                <DonutChart
                  series={dataCompanyContributions.series}
                  options={dataCompanyContributions}
                />
              </Col>
            ) : (
              <Col className="pt-12 px-2">
                <DonutCustomLegend
                  options={dataCompanyContributions}
                  hideAmount={true}
                  hideBenefitText={true}
                  isFooterRowTransparent={true}
                />
              </Col>
            )}
          </div>
        </Col>
        <Col className="mt-12 col-12 col-lg-6">
          <FingerTipsLinks data={fingertipLinks} />
        </Col>
      </Row>
    </Container>
  );
}

function Dashboard() {
  return (
    <DashboardProvider>
      <DashboardView />
    </DashboardProvider>
  );
}

export default Dashboard;
